/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import isEqual from 'lodash.isequal';
import moment from 'moment';
import {getHistoryPresetRange} from '@ion/dls/src/maps/components/timeMode/current/historyRange';
import {BoundaryMode, DateSelectorValues} from '@ion/dls/src/types/types';
import {
  TrackingFavoriteDateState,
  TrackingFavoriteFilterBucket,
  TrackingFavoriteFilterState,
  TrackingFavoritePreferences,
  TrackingFilterDrawerCategoryId,
  TrackingFilterStateKey
} from './types';

type NormalizedTrackingFavoriteFilterState = Record<TrackingFilterStateKey, TrackingFavoriteFilterBucket>;

/**
 * Defines the maximum number of characters allowed for a tracking favorite filter name.
 */
export const TRACKING_FAVORITE_NAME_MAX_LENGTH = 64;

/**
 * Defines the stable favorite filter state buckets that are persisted for tracking filters.
 */
export const TRACKING_FAVORITE_FILTER_STATE_KEYS = [
  TrackingFilterStateKey.Groups,
  TrackingFilterStateKey.Assets,
  TrackingFilterStateKey.Devices,
  TrackingFilterStateKey.AssetTypes,
  TrackingFilterStateKey.AssetCategories,
  TrackingFilterStateKey.GeoFences,
  TrackingFilterStateKey.Routes,
  TrackingFilterStateKey.AssetStatus
];

/**
 * Defines validation error codes for tracking favorite filter names.
 */
export enum TrackingFavoriteNameValidationError {
  Duplicate = 'duplicate',
  Empty = 'empty',
  MaxLength = 'maxLength'
}

/**
 * Removes leading and trailing whitespace from a tracking favorite filter name.
 *
 * @param name Defines the favorite filter name to trim.
 * @returns The trimmed favorite filter name.
 */
export const trimTrackingFavoriteName = (name: string) => name.trim();

/**
 * Validates a tracking favorite filter name against empty, length, and duplicate rules.
 *
 * @param name        Defines the favorite filter name to validate.
 * @param favorites   Defines the existing saved favorite filters.
 * @param currentName Defines the current favorite name when editing or renaming.
 * @returns A validation error code, or null when the name is valid.
 */
export const validateTrackingFavoriteName = (
  name: string,
  favorites: TrackingFavoritePreferences,
  currentName?: string | null
) => {
  const trimmedName = trimTrackingFavoriteName(name);
  let error: TrackingFavoriteNameValidationError | null = null;
  if (!trimmedName) {
    error = TrackingFavoriteNameValidationError.Empty;
  } else if (trimmedName.length > TRACKING_FAVORITE_NAME_MAX_LENGTH) {
    error = TrackingFavoriteNameValidationError.MaxLength;
  } else if (trimmedName !== currentName && favorites[trimmedName]) {
    error = TrackingFavoriteNameValidationError.Duplicate;
  }
  return error;
};

/**
 * Normalizes a tracking favorite filter state so each persisted filter bucket exists.
 *
 * @param state Defines the favorite filter state to normalize.
 * @returns A favorite filter state with every supported bucket and selected values.
 */
export const normalizeTrackingFavoriteState = (
  state: TrackingFavoriteFilterState = {}
) => (
  TRACKING_FAVORITE_FILTER_STATE_KEYS.reduce((normalizedState, key) => {
    const filterState = state[key];
    return {
      ...normalizedState,
      [key] : {
        selected : filterState ? filterState.selected : []
      }
    };
  }, {} as NormalizedTrackingFavoriteFilterState)
);

/**
 * Compares two tracking favorite filter states after normalizing missing buckets.
 *
 * @param first  Defines the first favorite filter state.
 * @param second Defines the second favorite filter state.
 * @returns True when both normalized favorite filter states are equal.
 */
export const areTrackingFavoriteStatesEqual = (
  first: TrackingFavoriteFilterState,
  second: TrackingFavoriteFilterState
) => isEqual(
  normalizeTrackingFavoriteState(first),
  normalizeTrackingFavoriteState(second)
);

/**
 * Resolves drawer category ids that contain selected values in a favorite filter state.
 *
 * @param state Defines the favorite filter state to inspect.
 * @returns Drawer category ids that should be expanded for the favorite.
 */
export const getTrackingFavoriteSelectedCategoryIds = (
  state: TrackingFavoriteFilterState
) => {
  const normalizedState = normalizeTrackingFavoriteState(state);
  const categoryIds: string[] = [];
  if (
    normalizedState.groups.selected.length ||
    normalizedState.assets.selected.length ||
    normalizedState.devices.selected.length
  ) {
    categoryIds.push(TrackingFilterDrawerCategoryId.GroupsAssets);
  }
  if (normalizedState.assetTypes.selected.length) {
    categoryIds.push(TrackingFilterDrawerCategoryId.AssetType);
  }
  if (normalizedState.assetCategories.selected.length) {
    categoryIds.push(TrackingFilterDrawerCategoryId.Category);
  }
  if (normalizedState.geoFences.selected.length) {
    categoryIds.push(TrackingFilterDrawerCategoryId.Geofence);
  }
  if (normalizedState.routes.selected.length) {
    categoryIds.push(TrackingFilterDrawerCategoryId.Routes);
  }
  if (normalizedState.assetStatus.selected.length) {
    categoryIds.push(TrackingFilterDrawerCategoryId.AssetStatus);
  }
  return categoryIds;
};

/**
 * Defines the History range a favorite runs its report over once applied.
 */
export interface TrackingFavoriteHistoryRange {
  preset: DateSelectorValues;
  from: Date;
  to: Date;
}

/**
 * Builds the History range a favorite saved now keeps - the running History
 * report's preset and range - in the shape legacy saved filters keep theirs.
 *
 * @param preset Defines the running History report's preset.
 * @param from   Defines the start of the range it runs over.
 * @param to     Defines the end of the range it runs over.
 * @returns The favorite's date state.
 */
export const getTrackingFavoriteDateState = (
  preset: DateSelectorValues,
  from: Date | string,
  to: Date | string
): TrackingFavoriteDateState => ({
  period    : preset,
  startDate : moment(from).toISOString(),
  endDate   : moment(to).toISOString()
});

/**
 * The date state a favorite saved over Live keeps - the period alone, as
 * legacy saved filters keep it - so applying it later returns to Live, as a
 * legacy saved filter does.
 */
export const TRACKING_FAVORITE_LIVE_DATE_STATE: TrackingFavoriteDateState = {
  period    : DateSelectorValues.Live,
  startDate : null,
  endDate   : null
};

/**
 * Determines whether a favorite was saved over Live - and so returns to
 * Live once applied, as a legacy saved filter does.
 *
 * @param state Defines the favorite filter state to inspect.
 * @returns `true` when the favorite keeps Live as its period, otherwise
 *          `false` - a favorite that keeps no period at all included.
 */
export const isTrackingFavoriteLive = (state?: TrackingFavoriteFilterState | null): boolean => (
  state?.date?.period === DateSelectorValues.Live
);

/**
 * Resolves the History range a favorite runs over once applied. A preset is
 * read as of now - Last 7 Days is the last seven days whenever it is loaded -
 * and a custom range as the dates it was saved with.
 *
 * @param state Defines the favorite filter state to inspect.
 * @returns The preset and range to run History over, or null when the
 *          favorite was saved over Live or keeps no range at all.
 */
export const getTrackingFavoriteHistoryRange = (
  state?: TrackingFavoriteFilterState | null
): TrackingFavoriteHistoryRange | null => {
  const {period, startDate, endDate} = state?.date ?? {} as Partial<TrackingFavoriteDateState>;
  let range: TrackingFavoriteHistoryRange | null = null;
  if (period === DateSelectorValues.Custom && startDate && endDate) {
    range = {preset: period, from: moment(startDate).toDate(), to: moment(endDate).toDate()};
  } else if (period && period !== DateSelectorValues.Live && period !== DateSelectorValues.Custom) {
    range = {preset: period, ...getHistoryPresetRange(period)};
  }
  return range;
};

/**
 * Defines the time mode and boundary a favorite keeps beside its filters -
 * the Time Mode control's History range (or Live) and the drawn boundary or
 * vicinity search, the two widgets on the map that filter it as well.
 */
export interface TrackingFavoriteWidgetState {
  date: TrackingFavoriteDateState;
  boundary: unknown[];
  boundaryMode?: BoundaryMode;
}

/**
 * Defines the map's widget readings a favorite's widget state is taken from.
 */
export interface TrackingFavoriteWidgetReadings {
  isHistoryRunning: boolean;
  historyPreset?: DateSelectorValues;
  historyFrom?: Date | string;
  historyTo?: Date | string;
  boundary?: unknown[];
  boundaryMode?: BoundaryMode;
}

/**
 * Builds the widget state a favorite saved now keeps, or is compared against:
 * the running History report's range, or Live, and the boundary applied.
 *
 * @param readings Defines the map's own widget readings.
 * @returns The favorite's widget state.
 */
export const getTrackingFavoriteWidgetState = ({
  isHistoryRunning,
  historyPreset,
  historyFrom,
  historyTo,
  boundary = [],
  boundaryMode
}: TrackingFavoriteWidgetReadings): TrackingFavoriteWidgetState => ({
  date : isHistoryRunning
    ? getTrackingFavoriteDateState(historyPreset, historyFrom, historyTo)
    : TRACKING_FAVORITE_LIVE_DATE_STATE,
  boundary,
  ...(boundary.length ? {boundaryMode} : {})
});

// the same minute, a saved custom range's dates having been written as text ...
const isSameMinute = (first?: Date | string | null, second?: Date | string | null) => (
  !!first && !!second && moment(first).isSame(moment(second), 'minute')
);

// a preset compares by its period - Last 7 Days is the last seven days
// whenever it runs - and a custom range by its dates. A favorite that keeps
// no period at all keeps no time mode, so any time mode matches it ...
const areFavoriteDatesEqual = (saved?: TrackingFavoriteDateState, current?: TrackingFavoriteDateState) => {
  let isEqualDate = !saved?.period;
  if (saved?.period && saved.period === current?.period) {
    isEqualDate = saved.period !== DateSelectorValues.Custom || (
      isSameMinute(saved.startDate, current.startDate) && isSameMinute(saved.endDate, current.endDate)
    );
  }
  return isEqualDate;
};

/**
 * Compares a saved favorite's time mode and boundary with the map's own. A
 * favorite saved before it kept either one matches on that one, so no older
 * favorite reads as changed only for having kept less.
 *
 * @param saved   Defines the saved favorite filter state.
 * @param current Defines the map's widget state now.
 * @returns `true` when the History range (or Live) and the boundary are the
 *          ones the favorite keeps, otherwise `false`.
 */
export const areTrackingFavoriteWidgetsEqual = (
  saved: TrackingFavoriteFilterState,
  current: TrackingFavoriteWidgetState
): boolean => (
  areFavoriteDatesEqual(saved?.date, current.date) &&
  (saved?.boundary === undefined || isEqual(saved.boundary, current.boundary))
);

/**
 * Determines whether a favorite keeps a boundary of its own - a drawn
 * boundary or a vicinity search, or none at all (an empty one) - rather than
 * having been saved before favorites kept one.
 *
 * @param state Defines the favorite filter state to inspect.
 * @returns `true` when the favorite keeps a boundary, empty or not.
 */
export const hasTrackingFavoriteBoundary = (state?: TrackingFavoriteFilterState | null): boolean => (
  Array.isArray(state?.boundary)
);
